import gql from 'graphql-tag';

import { Query, Mutation, QueryRegistry } from 'core';

import { CONTENT_FIELDS } from 'apps/content/gql';

export const ISSUE_FIELDS = gql`
  ${CONTENT_FIELDS}
  fragment issueFields on Issue {
    id
    canUpdate
    canDelete
    canAddDeleteLabels
    number
    title
    slug
    status
    description
    content {
      ...contentFields
    }
    labels {
      id
      name
      color
    }
    creatorProfile {
      id
      username
      avatar {
        id
        path
        url
      }
      imageFallbackChar
    }
    dateCreated
    lastUpdated
    commenters {
      id
      username
      avatar {
        id
        path
        url
      }
      imageFallbackChar
    }
    inSpace {
      id
      spaceType
      whichTypes
      content {
        id
        title
        type
        slug
        avatar {
          id
          path
          url
          completed
          private
        }
      }
      parent {
        id
        spaceType
        content {
          id
          slug
          whitelabel
        }
      }
    }
    comments {
      totalCount
    }
    assignees {
      id
      username
      imageFallbackChar
      avatar {
        id
        path
        url
      }
    }
    tags {
      id
      name
    }
  }
`;

export const ISSUE_CONNECTION_FIELDS = gql`
  ${ISSUE_FIELDS}
  fragment issueConnectionFields on IssueConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        ...issueFields
      }
    }
  }
`;

export const ISSUE_MUTATION = gql`
  ${ISSUE_FIELDS}
  mutation NewIssue($issueInput: IssueInput) {
    issue(issueData: $issueInput) {
      issue {
        ...issueFields
      }
    }
  }
`;

export class IssueMutation extends Mutation {
  static conf = {
    GQL: ISSUE_MUTATION,
    inputName: 'issueInput',
    resultPath: 'issue.issue',
  };
}

export const LABEL_MUTATION = gql`
  mutation NewLabel($labelInput: LabelInput) {
    label(labelData: $labelInput) {
      label {
        id
        name
        color
        restricted
        project {
          tracker {
            id
            labels {
              id
              name
              color
            }
          }
        }
      }
    }
  }
`;

export class LabelMutation extends Mutation {
  static conf = {
    GQL: LABEL_MUTATION,
    inputName: 'labelInput',
    resultPath: 'label.label',
  };
}

export const ISSUE_TRACKER_QUERY = gql`
  ${ISSUE_CONNECTION_FIELDS}
  query IssueTracker(
    $space: String
    $slug: String
    $cursor: String
    $sortBy: String
    $batchSize: Int
    $filterBy: [[String]]
  ) {
    project(space: $space, slug: $slug) {
      id
      tracker {
        id
        labels {
          id
          canUpdate
          canDelete
          name
          color
        }
        issues(
          first: $batchSize
          after: $cursor
          sortBy: $sortBy
          filterBy: $filterBy
        ) {
          ...issueConnectionFields
        }
        openIssuesCount
        closedIssuesCount
      }
    }
  }
`;

export class IssueTrackerQuery extends Query {
  static conf = {
    GQL: ISSUE_TRACKER_QUERY,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize', 'filterBy'],
    resultPath: 'project.tracker',
  };
}

const TRACKER_LABELS_QUERY = gql`
  query TrackerLabels($space: String, $slug: String) {
    project(space: $space, slug: $slug) {
      id
      tracker {
        id
        labels {
          id
          name
          color
        }
      }
    }
  }
`;

export class TrackerLabelsQuery extends Query {
  static conf = {
    GQL: TRACKER_LABELS_QUERY,
    varNames: ['space', 'slug'],
    resultPath: 'project.tracker.labels',
  };
}
QueryRegistry.add('issueTracker.TrackerLabelsQuery', TrackerLabelsQuery);

export const PROJECT_ISSUES = gql`
  ${ISSUE_CONNECTION_FIELDS}
  query Issues(
    $space: String
    $slug: String
    $cursor: String
    $sortBy: String
    $batchSize: Int
    $filterBy: [[String]]
  ) {
    project(space: $space, slug: $slug) {
      id
      tracker {
        id
        issues(
          first: $batchSize
          after: $cursor
          sortBy: $sortBy
          filterBy: $filterBy
        ) {
          ...issueConnectionFields
        }
      }
    }
  }
`;

export class ProjectIssuesQuery extends Query {
  static conf = {
    GQL: PROJECT_ISSUES,
    varNames: ['space', 'slug', 'cursor', 'sortBy', 'batchSize', 'filterBy'],
    resultPath: 'project.tracker.issues',
  };
}

export const ISSUE = gql`
  ${ISSUE_FIELDS}
  query Issue($space: String, $slug: String, $issueSlug: String!) {
    project(space: $space, slug: $slug) {
      id
      tracker {
        id
        issue(issueSlug: $issueSlug) {
          ...issueFields
        }
      }
    }
  }
`;

export class IssueQuery extends Query {
  static conf = {
    GQL: ISSUE,
    varNames: ['space', 'slug', 'issueSlug'],
    resultPath: 'project.tracker.issue',
  };
}

export const ISSUES = gql`
  ${ISSUE_FIELDS}
  query Issues(
    $tag: String
    $filterBy: [[String]]
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    issues(
      tag: $tag
      filterBy: $filterBy
      first: $batchSize
      after: $cursor
      sortBy: $sortBy
    ) {
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
      edges {
        cursor
        node {
          ...issueFields
        }
      }
    }
  }
`;

export class IssuesQuery extends Query {
  static conf = {
    GQL: ISSUES,
    varNames: ['tag', 'filterBy', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'issues',
  };
}
